import { formatDate } from '../utility/dateUtils';
import { ReactComponent as ManualIcon } from '../assets/icons/add_circle_dark.svg';
import { ReactComponent as ImportIcon } from '../assets/icons/upload.svg';
import { ReactComponent as AssociateIcon } from '../assets/icons/vpn_lock.svg';
import { ReactComponent as PrivateNoteIcon } from '../assets/icons/anonymous-user.svg';
import { ReactComponent as PublicNoteIcon } from '../assets/icons/public-note-icon.svg';
import {
  forbiddenCharacters,
  isEmpty,
  validateEmail,
  validateLength,
  validateName,
} from '../utility/validation';
import { isArrayEmpty, trimString } from '../utility/helpers';
import http from '../utility/http';
import { FIELD_TYPES } from './fieldTypes';
import { API_VALIDATE_EMAILS } from './apiRoutes';
import { SINGLE_DAY_SAVE_FORMAT } from './date';

const {
  CHECKBOX,
  TEXT,
  RICH_TEXT_EDITOR,
  AUTOCOMPLETE,
  SELECT,
  DATE_PICKER,
  SWITCH,
} = FIELD_TYPES;

const TODAY = new Date();

export const GENDER_OPTIONS = [
  {
    id: 1,
    name: 'Male',
  },
  {
    id: 2,
    name: 'Female',
  },
  {
    id: 3,
    name: 'Other',
  },
];

export const ORGANIZATION_SETTINGS_PEOPLE_INFO_FIELDS = [
  {
    name: 'name',
    required: true,
    type: FIELD_TYPES.TEXT,
    translationKey: 'infoName',
  },
  {
    name: 'is_visible',
    translationKey: 'isVisible',
    type: FIELD_TYPES.CHECKBOX,
    isControlled: true,
  },
  {
    name: 'is_sensitive',
    translationKey: 'isSensitive',
    type: FIELD_TYPES.CHECKBOX,
    isControlled: true,
  },
];

export const SENSITIVE_DATA_DEFAULT = '***********';

export const PEOPLE_PARAM_SCOPE_VALUES = {
  ME: 'ME',
  ASSOCIATES: 'ASSOCIATES',
  ALL_REPORTS: 'ALL_REPORTS',
  DIRECT_REPORTS: 'DIR_REPORTS',
  SHARED_COLLAB: 'SHARED_COLLAB',
  SHARED_VIEW: 'SHARED_VIEW',
};

export const USER_BASIC_PERSONAL_INFORMATION = {
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  EMAIL: 'email',
  GENDER: 'gender',
  DATE_OF_BIRTH: 'date_of_birth',
  PLACE_OF_BIRTH: 'place_of_birth',
  RESIDENTIAL_ADDRESS: 'residential_address',
  ID_NUMBER: 'internal_id_number',
  NATIONAL_ID_NUMBER: 'national_id_number',
  STATUS: 'status',
  PROFESSION_TITLE: 'profession_title',
  YEARS_OF_EXPERIENCE: 'years_of_work_experience',
  EXPERIENCE: 'previous_work_experience',
  EDUCATION: 'education',
  SOCIAL_ACCOUNTS: 'social_accounts',
  EXTERNAL_RESOURCE: 'external_resource',
  LANGUAGE: 'language',
  TIME_ZONE: 'timezone',
};

export const USER_EMPLOYMENT_INFORMATION = {
  JOB_TITLE: 'position',
  EMPLOYMENT_DATE: 'date_of_employment',
  EMPLOYMENT_TERMINATION_DATE: 'employment_terminated_at',
  PRIVATE_HEALTH_INSURANCE: 'private_health_insurance',
  ROLE: 'role',
  FRAMEWORK: 'framework',
  TRACK: 'track',
  LEVEL: 'level',
  DIVISION: 'division',
  DEPARTMENT: 'department',
  REPORT_TO: 'report_to',
  WORKING_HOURS: 'working_hours',
  NET_SALARY: 'net_salary',
  GROSS_SALARY_1: 'gross_salary_1',
  GROSS_SALARY_2: 'gross_salary_2',
};

export const EXTERNAL_USER_INFO_KEYS = [
  USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME,
  USER_BASIC_PERSONAL_INFORMATION.LAST_NAME,
  USER_BASIC_PERSONAL_INFORMATION.EMAIL,
  USER_EMPLOYMENT_INFORMATION.REPORT_TO,
];

export const USER_PERSONAL_INFO_KEYS = [
  USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME,
  USER_BASIC_PERSONAL_INFORMATION.LAST_NAME,
  USER_BASIC_PERSONAL_INFORMATION.EMAIL,
  USER_BASIC_PERSONAL_INFORMATION.GENDER,
  USER_BASIC_PERSONAL_INFORMATION.PLACE_OF_BIRTH,
  USER_BASIC_PERSONAL_INFORMATION.ID_NUMBER,
  USER_BASIC_PERSONAL_INFORMATION.PROFESSION_TITLE,
  USER_BASIC_PERSONAL_INFORMATION.YEARS_OF_EXPERIENCE,
  USER_BASIC_PERSONAL_INFORMATION.EXPERIENCE,
  USER_BASIC_PERSONAL_INFORMATION.EDUCATION,
  USER_BASIC_PERSONAL_INFORMATION.SOCIAL_ACCOUNTS,
  USER_BASIC_PERSONAL_INFORMATION.EXTERNAL_RESOURCE,
  USER_BASIC_PERSONAL_INFORMATION.LANGUAGE,
  USER_BASIC_PERSONAL_INFORMATION.TIME_ZONE,
  USER_BASIC_PERSONAL_INFORMATION.DATE_OF_BIRTH,
  USER_BASIC_PERSONAL_INFORMATION.RESIDENTIAL_ADDRESS,
  USER_BASIC_PERSONAL_INFORMATION.NATIONAL_ID_NUMBER,
];

export const USER_EMPLOYMENT_INFO_KEYS = [
  USER_EMPLOYMENT_INFORMATION.JOB_TITLE,
  USER_EMPLOYMENT_INFORMATION.EMPLOYMENT_DATE,
  USER_EMPLOYMENT_INFORMATION.FRAMEWORK,
  USER_EMPLOYMENT_INFORMATION.TRACK,
  USER_EMPLOYMENT_INFORMATION.LEVEL,
  USER_EMPLOYMENT_INFORMATION.DIVISION,
  USER_EMPLOYMENT_INFORMATION.DEPARTMENT,
  USER_EMPLOYMENT_INFORMATION.REPORT_TO,
  USER_EMPLOYMENT_INFORMATION.WORKING_HOURS,
  USER_EMPLOYMENT_INFORMATION.PRIVATE_HEALTH_INSURANCE,
  USER_EMPLOYMENT_INFORMATION.NET_SALARY,
  USER_EMPLOYMENT_INFORMATION.GROSS_SALARY_1,
  USER_EMPLOYMENT_INFORMATION.GROSS_SALARY_2,
];

export const PEOPLE_DEFAULT_ORDERING = {
  ordering: `${USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME},id`,
};

export const PEOPLE_MAX_PAGE_SIZE = {
  page_size: 500,
};

export const INITIAL_SORT = {
  column: 'name',
  asc: true,
  sortKey: `${USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME},id`,
};

export const ADD_USER_OPTION_KEYS = {
  MANUAL: 'MANUAL',
  IMPORT: 'IMPORT',
  ASSOCIATE: 'ASSOCIATE',
};

export const USER_VALIDATIONS = {
  [USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME]: [
    {
      validator: isEmpty,
      type: 'required',
    },
    {
      validator: validateName,
      type: 'invalidName',
    },
    {
      validator: value => validateLength(value, 2, undefined, true),
      type: 'minLength',
    },
    {
      validator: value => validateLength(value, 0, 50),
      type: 'maxLength',
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.LAST_NAME]: [
    {
      validator: isEmpty,
      type: 'required',
    },
    {
      validator: validateName,
      type: 'invalidName',
    },
    {
      validator: value => validateLength(value, 2, undefined, true),
      type: 'minLength',
    },
    {
      validator: value => validateLength(value, 0, 50),
      type: 'maxLength',
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.EMAIL]: [
    {
      validator: isEmpty,
      type: 'required',
    },
    {
      validator: validateEmail,
      type: 'invalidEmail',
    },
    {
      validator: value => validateLength(value, 0, 100),
      type: 'exceededLengthEmail',
    },
    {
      type: 'availableEmail',
      async: true,
      shouldSkipValidation: true,
      validator: value => {
        const sanitizedUserEmail = trimString(value).toLowerCase();
        return http
          .post(API_VALIDATE_EMAILS, {
            emails: [sanitizedUserEmail],
          })
          .then(({ data }) => !data?.taken?.includes(sanitizedUserEmail));
      },
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.ID_NUMBER]: [
    {
      validator: value => validateLength(value, 0, 255),
      type: 'exceededLength',
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.NATIONAL_ID_NUMBER]: [
    {
      validator: value => validateLength(value, 0, 255),
      type: 'exceededLength',
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.PLACE_OF_BIRTH]: [
    {
      validator: value => validateLength(value, 0, 255),
      type: 'exceededLength',
    },
    {
      type: 'forbiddenCharacters',
      validator: forbiddenCharacters,
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.RESIDENTIAL_ADDRESS]: [
    {
      validator: value => validateLength(value, 0, 255),
      type: 'exceededLength',
    },
    {
      type: 'forbiddenCharacters',
      validator: forbiddenCharacters,
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.EDUCATION]: [
    {
      validator: value => validateLength(value, 0, 255),
      type: 'exceededLength',
    },
    {
      type: 'forbiddenCharacters',
      validator: forbiddenCharacters,
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.PROFESSION_TITLE]: [
    {
      validator: value => validateLength(value, 0, 255),
      type: 'exceededLength',
    },
    {
      type: 'forbiddenCharacters',
      validator: forbiddenCharacters,
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.YEARS_OF_EXPERIENCE]: [
    {
      validator: value => value < 50,
      type: 'exceededNumberOfYears',
    },
    {
      validator: value => value > 0,
      type: 'minValue',
    },
  ],
  [USER_BASIC_PERSONAL_INFORMATION.EXPERIENCE]: [
    {
      validator: fieldValue => validateLength(fieldValue, 0, 10000),
      type: 'descriptionMaxLength',
    },
  ],
  [USER_EMPLOYMENT_INFORMATION.WORKING_HOURS]: [
    {
      validator: value => value >= 0,
      type: 'invalidWorkingHours',
    },
    {
      validator: value => value <= 32767,
      type: 'exceeded',
    },
  ],
  [USER_EMPLOYMENT_INFORMATION.NET_SALARY]: [
    {
      validator: value => value > 0,
      type: 'minValue',
    },
    {
      validator: value => value <= 2147483647,
      type: 'exceeded',
    },
  ],
  [USER_EMPLOYMENT_INFORMATION.REPORT_TO]: [
    {
      validator: value => !isArrayEmpty(value),
      type: 'required',
    },
  ],
  [USER_EMPLOYMENT_INFORMATION.REPORT]: [
    {
      validator: isEmpty,
      type: 'required',
    },
  ],
  [USER_EMPLOYMENT_INFORMATION.TRACK]: [
    {
      validator: isEmpty,
      type: 'required',
    },
  ],
  [USER_EMPLOYMENT_INFORMATION.LEVEL]: [
    {
      validator: isEmpty,
      type: 'required',
    },
  ],
  [USER_EMPLOYMENT_INFORMATION.DEPARTMENT]: [
    {
      validator: isEmpty,
      type: 'required',
    },
  ],
};

export const ADD_USER_OPTIONS = [
  {
    id: ADD_USER_OPTION_KEYS.MANUAL,
    icon: ManualIcon,
    key: 'manual',
  },
  {
    id: ADD_USER_OPTION_KEYS.IMPORT,
    icon: ImportIcon,
    key: 'import',
    hasImport: true,
  },
  {
    id: ADD_USER_OPTION_KEYS.ASSOCIATE,
    icon: AssociateIcon,
    key: 'associate',
  },
];

export const USER_FIELDS = {
  FIRST_NAME: {
    name: USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME,
    key: 'firstName',
    type: TEXT,
    required: true,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME],
    ],
  },
  LAST_NAME: {
    name: USER_BASIC_PERSONAL_INFORMATION.LAST_NAME,
    key: 'lastName',
    type: TEXT,
    required: true,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.LAST_NAME],
    ],
  },
  EMAIL: {
    name: USER_BASIC_PERSONAL_INFORMATION.EMAIL,
    key: 'email',
    type: TEXT,
    required: true,
    validators: [...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.EMAIL]],
  },
  ID_NUMBER: {
    name: USER_BASIC_PERSONAL_INFORMATION.ID_NUMBER,
    key: 'internal_id_number',
    type: TEXT,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.ID_NUMBER],
    ],
  },
  NATIONAL_ID_NUMBER: {
    name: USER_BASIC_PERSONAL_INFORMATION.NATIONAL_ID_NUMBER,
    key: 'national_id_number',
    type: TEXT,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.NATIONAL_ID_NUMBER],
    ],
  },
  GENDER: {
    name: USER_BASIC_PERSONAL_INFORMATION.GENDER,
    key: 'gender',
    type: SELECT,
    optionsKey: 'genders',
    shouldReturnOption: true,
    parser: {
      value: 'id',
      label: 'name',
    },
  },
  DATE_OF_BIRTH: {
    name: USER_BASIC_PERSONAL_INFORMATION.DATE_OF_BIRTH,
    type: DATE_PICKER,
    getMaxDate: () =>
      new Date(
        +formatDate(TODAY, 'yyyy') - 18,
        TODAY.getMonth(),
        TODAY.getDate()
      ),
    valueFormat: SINGLE_DAY_SAVE_FORMAT,
  },
  PLACE_OF_BIRTH: {
    name: USER_BASIC_PERSONAL_INFORMATION.PLACE_OF_BIRTH,
    key: 'place_of_birth',
    type: TEXT,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.PLACE_OF_BIRTH],
    ],
  },
  RESIDENTIAL_ADDRESS: {
    name: USER_BASIC_PERSONAL_INFORMATION.RESIDENTIAL_ADDRESS,
    key: 'residential_address',
    type: TEXT,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.RESIDENTIAL_ADDRESS],
    ],
  },
  EDUCATION: {
    name: USER_BASIC_PERSONAL_INFORMATION.EDUCATION,
    key: 'education',
    type: TEXT,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.EDUCATION],
    ],
  },
  PROFESSION_TITLE: {
    name: USER_BASIC_PERSONAL_INFORMATION.PROFESSION_TITLE,
    key: 'profession_title',
    type: TEXT,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.PROFESSION_TITLE],
    ],
  },
  YEARS_OF_EXPERIENCE: {
    name: USER_BASIC_PERSONAL_INFORMATION.YEARS_OF_EXPERIENCE,
    key: 'years_of_work_experience',
    type: TEXT,
    inputType: 'number',
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.YEARS_OF_EXPERIENCE],
    ],
  },
  EXPERIENCE: {
    name: USER_BASIC_PERSONAL_INFORMATION.EXPERIENCE,
    key: 'experience',
    type: RICH_TEXT_EDITOR,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.EXPERIENCE],
    ],
  },
  PRIVATE_HEALTH_INSURANCE: {
    name: USER_EMPLOYMENT_INFORMATION.PRIVATE_HEALTH_INSURANCE,
    type: SWITCH,
    key: 'private_health_insurance',
    isToggleable: true,
    shouldRender: () => true,
  },
  SOCIAL_ACCOUNTS: {
    name: USER_BASIC_PERSONAL_INFORMATION.SOCIAL_ACCOUNTS,
    key: 'social_accounts',
    type: RICH_TEXT_EDITOR,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.EXPERIENCE],
    ],
  },
  EXTERNAL_RESOURCE: {
    name: USER_BASIC_PERSONAL_INFORMATION.EXTERNAL_RESOURCE,
    key: 'external_resource',
    type: RICH_TEXT_EDITOR,
    validators: [
      ...USER_VALIDATIONS[USER_BASIC_PERSONAL_INFORMATION.EXPERIENCE],
    ],
  },
  LANGUAGE: {
    name: USER_BASIC_PERSONAL_INFORMATION.LANGUAGE,
    key: 'language',
    type: SELECT,
    optionsKey: 'languages',
    shouldReturnOption: true,
    parser: {
      value: 'value',
      label: 'label',
    },
  },
  TIME_ZONE: {
    name: USER_BASIC_PERSONAL_INFORMATION.TIME_ZONE,
    key: 'timezone',
    type: SELECT,
    optionsKey: 'timezones',
    shouldReturnOption: true,
    parser: {
      value: 'value',
      label: 'label',
    },
  },
  REPORT_TO: {
    name: USER_EMPLOYMENT_INFORMATION.REPORT_TO,
    key: 'reportingTo',
    type: AUTOCOMPLETE,
    initialValue: '',
    optionsKey: 'reportToOptions',
    organizationUserKey: 'organizationUser',
    isAsync: true,
    isSingleSelect: true,
    hasAvatar: true,
    required: true,
    validators: [...USER_VALIDATIONS[USER_EMPLOYMENT_INFORMATION.REPORT_TO]],
  },
  REPORT: {
    name: USER_EMPLOYMENT_INFORMATION.REPORT_TO,
    key: 'reportingTo',
    type: SELECT,
    optionsKey: 'availableManagers',
    isUser: true,
    required: true,
    parser: {
      value: 'id',
      label: 'first_name',
    },
    validators: [...USER_VALIDATIONS[USER_EMPLOYMENT_INFORMATION.REPORT]],
  },
  STATUS: {
    name: USER_BASIC_PERSONAL_INFORMATION.STATUS,
    type: SELECT,
    optionsKey: 'statuses',
    parser: {
      value: 'value',
      label: 'name',
    },
    isSearchDisabled: true,
  },
  WORKING_HOURS: {
    name: USER_EMPLOYMENT_INFORMATION.WORKING_HOURS,
    key: 'working_hours',
    type: TEXT,
    inputType: 'number',
    validators: [
      ...USER_VALIDATIONS[USER_EMPLOYMENT_INFORMATION.WORKING_HOURS],
    ],
  },
  NET_SALARY: {
    name: USER_EMPLOYMENT_INFORMATION.NET_SALARY,
    key: 'net_salary',
    type: TEXT,
    inputType: 'number',
    validators: [...USER_VALIDATIONS[USER_EMPLOYMENT_INFORMATION.NET_SALARY]],
  },
  GROSS_SALARY_1: {
    name: USER_EMPLOYMENT_INFORMATION.GROSS_SALARY_1,
    key: 'gross_salary_1',
    type: TEXT,
    inputType: 'number',
    validators: [...USER_VALIDATIONS[USER_EMPLOYMENT_INFORMATION.NET_SALARY]],
  },
  GROSS_SALARY_2: {
    name: USER_EMPLOYMENT_INFORMATION.GROSS_SALARY_2,
    key: 'gross_salary_2',
    type: TEXT,
    inputType: 'number',
    validators: [...USER_VALIDATIONS[USER_EMPLOYMENT_INFORMATION.NET_SALARY]],
  },
  JOB_TITLE: {
    name: USER_EMPLOYMENT_INFORMATION.JOB_TITLE,
    type: SELECT,
    optionsKey: 'positions',
    parser: {
      value: 'id',
      label: 'name',
    },
  },
  FRAMEWORK: {
    name: USER_EMPLOYMENT_INFORMATION.FRAMEWORK,
    type: SELECT,
    optionsKey: 'frameworks',
    parser: {
      value: 'id',
      label: 'name',
    },
    dependants: [
      {
        name: USER_EMPLOYMENT_INFORMATION.TRACK,
      },
    ],
  },
  TRACK: {
    name: USER_EMPLOYMENT_INFORMATION.TRACK,
    type: SELECT,
    optionsKey: 'tracks',
    parser: {
      value: 'id',
      label: 'name',
    },
    dependants: [
      {
        name: USER_EMPLOYMENT_INFORMATION.LEVEL,
      },
    ],
    parent: {
      name: USER_EMPLOYMENT_INFORMATION.FRAMEWORK,
      options: 'frameworks',
    },
    validators: [...USER_VALIDATIONS[USER_EMPLOYMENT_INFORMATION.TRACK]],
  },
  LEVEL: {
    name: USER_EMPLOYMENT_INFORMATION.LEVEL,
    type: SELECT,
    optionsKey: 'levels',
    parser: {
      value: 'id',
      label: 'name',
    },
    parent: {
      name: USER_EMPLOYMENT_INFORMATION.TRACK,
      options: 'tracks',
    },
    validators: [...USER_VALIDATIONS[USER_EMPLOYMENT_INFORMATION.LEVEL]],
  },
  DIVISION: {
    name: USER_EMPLOYMENT_INFORMATION.DIVISION,
    type: SELECT,
    optionsKey: 'divisions',
    parser: {
      value: 'id',
      label: 'name',
    },
    dependants: [
      {
        name: USER_EMPLOYMENT_INFORMATION.DEPARTMENT,
        value: null,
      },
    ],
  },
  DEPARTMENT: {
    name: USER_EMPLOYMENT_INFORMATION.DEPARTMENT,
    type: SELECT,
    optionsKey: 'departments',
    parser: {
      value: 'id',
      label: 'name',
    },
    parent: {
      name: USER_EMPLOYMENT_INFORMATION.DIVISION,
      options: 'divisions',
    },
    validators: [...USER_VALIDATIONS[USER_EMPLOYMENT_INFORMATION.DEPARTMENT]],
  },
  EMPLOYMENT_DATE: {
    name: USER_EMPLOYMENT_INFORMATION.EMPLOYMENT_DATE,
    type: DATE_PICKER,
    valueFormat: SINGLE_DAY_SAVE_FORMAT,
  },
  INVITED: {
    name: 'status',
    key: 'invited',
    type: CHECKBOX,
  },
  ROLE: {
    name: USER_EMPLOYMENT_INFORMATION.ROLE,
    key: 'role',
    type: SELECT,
    optionsKey: 'roles',
    parser: {
      value: 'value',
      label: 'label',
    },
  },
};

export const USER_VIEW_OPTIONS = [
  {
    value: 'active',
    name: 'Active',
    key: 'active',
  },
  {
    value: 'deactivated',
    name: 'Deactivated',
    key: 'deactivated',
  },
  {
    value: 'external',
    name: 'External',
    key: 'external',
  },
];

export const ACTIVE_DEFAULT_NAME = 'active';

export const DEACTIVATED_DEFAULT_NAME = 'deactivated';

export const EXTERNAL_DEFAULT_NAME = 'external';

export const NOTE_TYPES = {
  PUBLIC: 'PUBLIC',
  PRIVATE: 'PRIVATE',
};

export const NOTE_TYPES_MAP = [
  {
    id: NOTE_TYPES.PRIVATE,
    name: 'Private',
    key: 'private',
    icon: PrivateNoteIcon,
  },
  {
    id: NOTE_TYPES.PUBLIC,
    name: 'Public',
    key: 'public',
    icon: PublicNoteIcon,
  },
];

export const LANGUAGES = [
  { label: 'English', value: 'en' },
  { label: 'Serbian', value: 'sr' },
  { label: 'German', value: 'de' },
  { label: 'French', value: 'fr' },
  { label: 'Spanish', value: 'es' },
  { label: 'Italian', value: 'it' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Russian', value: 'ru' },
  { label: 'Chinese', value: 'zh' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Korean', value: 'ko' },
  { label: 'Arabic', value: 'ar' },
  { label: 'Hindi', value: 'hi' },
  { label: 'Dutch', value: 'nl' },
  { label: 'Polish', value: 'pl' },
  { label: 'Turkish', value: 'tr' },
  { label: 'Czech', value: 'cs' },
  { label: 'Hungarian', value: 'hu' },
  { label: 'Romanian', value: 'ro' },
  { label: 'Greek', value: 'el' },
  { label: 'Swedish', value: 'sv' },
  { label: 'Norwegian', value: 'no' },
  { label: 'Danish', value: 'da' },
  { label: 'Finnish', value: 'fi' },
  { label: 'Ukrainian', value: 'uk' },
  { label: 'Bulgarian', value: 'bg' },
  { label: 'Croatian', value: 'hr' },
  { label: 'Slovak', value: 'sk' },
  { label: 'Slovenian', value: 'sl' },
];
